import { ProjectPromocode } from '@/types/marketplace'
import { formatPromocodeDiscount, PromocodeDiscountSource } from '@/helpers/billing/promocodeDiscountLabel'

export interface ActivePromocodeText {
    title: string
    scope: string
    expiresAt: string
    /** Пусто, если промокод дал скидку в текущем счёте или счёт ещё не рассчитан. */
    notAppliedHint: string
}

const pad = (value: number) => String(value).padStart(2, '0')

/** «31.12.2026 23:59» в часовом поясе браузера; пусто для некорректной даты. */
export function formatPromocodeExpiration(value: string | null | undefined): string {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
        date.getHours()
    )}:${pad(date.getMinutes())}`
}

/** На что действует промокод: «тариф», «тариф «Pro»», ««Чат», «Звонобот»», «весь заказ» — и при оплате чего. */
function describeScope(promocode: ProjectPromocode, integrationNames: Record<string, string | undefined>) {
    const named = (promocode.integrationIds ?? []).map(id => `«${integrationNames[id] || id}»`).join(', ')
    if (promocode.scope === 'order') return { scope: 'Скидка на весь заказ', payTarget: '' }
    if (promocode.scope === 'integration') return { scope: `Скидка на ${named}`, payTarget: named }
    return named
        ? { scope: `Скидка на тариф ${named}`, payTarget: `тарифа ${named}` }
        : { scope: 'Скидка на тариф', payTarget: 'тарифа' }
}

/**
 * Тексты про активный промокод проекта для формы оплаты. Промокод действует на тариф, на выбранные
 * интеграции или на весь заказ, поэтому активированный промокод может не дать скидки в текущем счёте —
 * об этом говорим прямо, иначе «Промокод активирован» без строки скидки выглядит как ошибка.
 */
export function describeActivePromocode(params: {
    promocode: ProjectPromocode
    integrationNames: Record<string, string | undefined>
    missesOrder: boolean
}): ActivePromocodeText {
    const { promocode, integrationNames, missesOrder } = params
    const { scope, payTarget } = describeScope(promocode, integrationNames)
    const expiresAt = formatPromocodeExpiration(promocode.expirationDate)
    const discount = formatPromocodeDiscount(promocode)

    return {
        title: `Активен промокод ${promocode.promocode}${discount ? ` ${discount}` : ''}`,
        scope,
        expiresAt: expiresAt ? `Действует до ${expiresAt}` : '',
        notAppliedHint: !missesOrder
            ? ''
            : payTarget
            ? `На этот счёт промокод не действует: скидка применится при оплате ${payTarget}.`
            : 'На этот счёт промокод не действует.',
    }
}

/**
 * Промокод, который даст скидку на тариф, — тот же, что выберет billing: промокоды приходят в его порядке
 * (самые узкие первыми), на тариф действуют тарифные (все или из списка) и промокод на весь заказ.
 * activePromocode витрины — один на все тарифы, поэтому на тарифе вне списка подпись была бы чужой.
 */
export function findTariffPromocode(
    promocodes: ProjectPromocode[],
    tariffId: string
): ProjectPromocode | undefined {
    return promocodes.find(promocode => {
        const scope = promocode.scope ?? 'tariff'
        if (scope === 'order') return true
        if (scope !== 'tariff') return false
        return !promocode.integrationIds?.length || promocode.integrationIds.includes(tariffId)
    })
}

/**
 * «Промокод SALE: скидка 1 000 ₽ на оплату» — вместо цены со скидкой у промокода на сумму. Сумма даётся
 * один раз на заказ, а тариф показан ценой за месяц или период: при помесячной оплате первое списание —
 * за 2 месяца, и «цена минус вся сумма» обещала бы больше, чем даст заказ. Для процента — пусто.
 */
export function describeAmountPromocode(
    promocode: (PromocodeDiscountSource & { promocode: string }) | null | undefined
): string {
    if (promocode?.discountType !== 'amount') return ''
    const amount = new Intl.NumberFormat('ru-RU').format(Number(promocode.discountAmount ?? 0))
    return `Промокод ${promocode.promocode}: скидка ${amount} ₽ на оплату`
}

/**
 * Итог активации: новый промокод мог заменить активные на те же позиции и мог не дать скидки в этом
 * счёте — о том и другом говорим прямо.
 */
export function describeActivation(params: { replacedPromocodes: string[]; missesOrder: boolean }): string {
    const replaced = params.replacedPromocodes.length
        ? ` и заменил ${params.replacedPromocodes.join(', ')}`
        : ''
    const misses = params.missesOrder ? ', но на этот счёт не действует' : ''
    return `Промокод активирован${replaced}${misses}`
}
